import { defineStore } from 'pinia'
import { SAVE_DATA_LOCAL_STORAGE, LOAD_STORED_DATA, CLEAR_LOCAL_STORAGE, LOAD_SUGGESTION_DATA } from '@/utils/localStorage'
import { STORAGE_CUSTOMIZATION_KEY, STORAGE_TRANSLATIONS_KEY, DEFAULT_LANGUAGE, STORAGE_LANG_KEY } from '@/config'
import { Auth, config } from '@/dashboards'
import axios from 'axios'

function getTranslations (lang, translations) {
  translations = translations || {}
  if (translations[lang]) return translations[lang]
  if (translations[DEFAULT_LANGUAGE]) return translations[DEFAULT_LANGUAGE]
  return Object.values(translations)[0] || {}
}

function getQueryParams () {
  const urlSearchParams = new URLSearchParams(window.location.search)
  const query = Object.fromEntries(urlSearchParams.entries())
  delete query.forgot_success
  return query
}

function getClubUuid (data) {
  return typeof data.club === 'string'
    ? data.club
    : (
        data.brand_favorite_store?.card_uuid ||
        data.club?.card_uuid ||
        data.club?.[0]?.card_uuid
      )
}

export const useCommonAuth = defineStore('commonAuth', {
  state: () => ({
    isFetching: true,
    isFetchingToken: true,
    isFetchingFavStore: false,
    isSendingAutologin: false,
    isSendingCustomerCode: false,
    loginMethods: ['email', 'dni', 'link'],
    isSubmiting: false,
    redirecting: false,
    error: false,
    isValidParams: false,
    loginParams: getQueryParams(),
    customization: {},
    translations: {},
    suggestions: LOAD_SUGGESTION_DATA(),
    language: '',
    authType: '', // farmacloud | store | customer
    farmacloudLogin: false,
    signup: false,
    rootSite: false,
    maintenance: false
  }),
  actions: {
    async setLoginParams (useCase) {
      try {
        this.isFetching = true
        this.isValidParams = false
        CLEAR_LOCAL_STORAGE()
        const data = await Auth.setLoginParams.call(this, { ...this.loginParams, use_case: useCase })
        if (!this.loginParams.club) this.loginParams.club = getClubUuid(data)
        this.isValidParams = true
        if (!data) return
        if (data.redirect_uri) {
          window.location.assign(data.redirect_uri)
          throw new Error('redirecting')
        }
        data.customization && this.SET_CUSTOMIZATION(data.customization)
        data.translations && this.SET_TRANSLATIONS(data.translations)
        return data
      } catch (err) {
        if (err.message === 'redirecting') {
          this.redirecting = true
          return
        }
        console.error(err)
        this.error = true
        CLEAR_LOCAL_STORAGE()
      } finally {
        this.isFetching = false
        this.isFetchingToken = false
      }
    },
    async loadSession (sessionId, context) {
      try {
        this.isFetching = true
        this.isValidParams = false
        this.LOAD_STORED_CUSTOMIZATION()
        this.LOAD_STORED_TRANSLATIONS()
        const data = await Auth.loadSession.call(this, sessionId, context)
        this.isValidParams = true
        if (!data) return
        this.SET_LOGIN_PARAMS(data)
        data.customization && this.SET_CUSTOMIZATION(data.customization)
        data.translations && this.SET_TRANSLATIONS(data.translations)
        return data
      } catch (err) {
        if (err.message === 'redirecting') {
          this.redirecting = true
          return
        }
        console.error(err)
        this.error = true
        this.isFetchingToken = false
        CLEAR_LOCAL_STORAGE()
      } finally {
        this.isFetching = false
      }
    },
    async getTemporalCustomer (hash, validation) {
      let url = `${config.GATEWAY_BASE_URL}customer/v1/temporal/customer/${hash}`
      if (validation) url += `/${validation}`
      const { data } = await axios.get(url)
      this.isValidParams = true
      return data
    },
    async getCustomization () {
      try {
        this.error = false
        this.isFetching = true
        const { data } = await axios.get(`${config.OAUTH_BASE_URL}/customization`, { withCredentials: true })
        this.SET_CUSTOMIZATION(data)
      } catch {
        this.error = true
      } finally {
        this.isFetching = false
      }
    },
    async checkToken () {
      try {
        if (this.redirecting) return
        this.isFetchingToken = true
        await Auth.checkToken.call(this)
        this.isFetchingToken = false
        return true
      } catch (err) {
        this.redirecting = err.message === 'redirecting'
        this.isFetchingToken = false
        return false
      }
    },
    async setFavoriteStore ({ sessionId, storeId }) {
      try {
        this.isFetchingFavStore = true
        await Auth.setFavoriteStore.call(this, { sessionId, storeId })
      } catch {
        this.error = true
      } finally {
        this.isFetchingFavStore = false
      }
    },
    async sendAutologin (email) {
      try {
        this.isSendingAutologin = true
        await Auth.sendAutologin.call(this, email)
      } catch (err) {
        console.error(err)
      } finally {
        this.isSendingAutologin = false
      }
    },
    async sendCustomerCode (dni) {
      try {
        this.isSendingCustomerCode = true
        await Auth.sendCustomerCode.call(this, dni)
      } catch (err) {
        console.error(err)
      } finally {
        this.isSendingCustomerCode = false
      }
    },
    SET_LANGUAGE (payload) {
      this.language = payload
      localStorage.setItem(STORAGE_LANG_KEY, payload)
    },
    SET_CUSTOMIZATION (payload = {}) {
      this.customization = SAVE_DATA_LOCAL_STORAGE(STORAGE_CUSTOMIZATION_KEY, payload)
    },
    SET_TRANSLATIONS (payload) {
      this.translations = SAVE_DATA_LOCAL_STORAGE(STORAGE_TRANSLATIONS_KEY, getTranslations(this.language, payload))
    },
    LOAD_STORED_CUSTOMIZATION () {
      this.customization = LOAD_STORED_DATA(STORAGE_CUSTOMIZATION_KEY) || {}
    },
    LOAD_STORED_TRANSLATIONS () {
      this.translations = LOAD_STORED_DATA(STORAGE_TRANSLATIONS_KEY) || {}
    },
    SET_LOGIN_PARAMS (payload) {
      this.loginParams = {
        client_id: payload.client_id,
        response_type: payload.response_type,
        redirect_uri: payload.redirect_uri,
        scope: payload.scope,
        state: payload.state,
        check_perms: payload.check_perms,
        store: payload.store,
        sso: payload.sso,
        without_farmacloud: payload.without_farmacloud || payload.withoutFarmacloud || undefined,
        club: getClubUuid(payload),
        ext_id: payload.ext_id,
        sku: payload.sku,
        loyalty_id: payload.loyalty_id,
        type: payload.type
      }
    }
  }
})
